import { useEffect, useState } from 'react'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import {
  Busy,
  ErrorBanner,
  Field,
  MultiReferenceSelect,
  Notice,
  NumberInput,
} from '../ui/controls'
import type { MessageKey } from '../i18n/messages'
import type {
  Importances,
  PreferenceImportance,
  PreferenceReferenceCatalogue,
  Preferences,
  ProfileReferenceCatalogue,
} from '../api/types'

const IMPORTANCE_FIELDS: readonly (keyof Importances)[] = [
  'age',
  'height',
  'location',
  'identity',
  'maritalStatus',
  'children',
  'fertility',
  'education',
  'employment',
  'income',
  'language',
  'religion',
  'smoking',
  'alcohol',
]

const IMPORTANCE_LABEL: Record<keyof Importances, MessageKey> = {
  age: 'preferences.ageRange',
  height: 'preferences.heightRange',
  location: 'profile.location',
  identity: 'profile.nzStatus',
  maritalStatus: 'profile.maritalStatus',
  children: 'onboarding.step.profile',
  fertility: 'profile.maritalStatus',
  education: 'profile.education',
  employment: 'profile.employment',
  income: 'profile.occupation',
  language: 'profile.section.basics',
  religion: 'profile.religion',
  smoking: 'profile.smoking',
  alcohol: 'profile.alcohol',
}

/**
 * The preference form.
 *
 * The importance levels come from `/api/reference/preferences`, which sends an order and nothing
 * else — 契约 §8.2 is explicit that weights and thresholds stay on the server, and 决议 §3.3
 * keeps exact scores off every layer. So this screen can say that Important outranks Prefer, and
 * genuinely cannot say what either is worth.
 */
export function PreferencesPage() {
  const { t } = useI18n()

  const [catalogue, setCatalogue] = useState<PreferenceReferenceCatalogue | null>(null)
  const [profileCatalogue, setProfileCatalogue] = useState<ProfileReferenceCatalogue | null>(null)
  const [prefs, setPrefs] = useState<Preferences | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    Promise.all([api.reference.preferences(), api.reference.profile()])
      .then(([prefCat, profCat]) => {
        setCatalogue(prefCat)
        setProfileCatalogue(profCat)
      })
      .catch(setError)

    api.preferences
      .get()
      .then(setPrefs)
      .catch((cause) => {
        if (isNotFound(cause)) {
          setPrefs(emptyPreferences())
          return
        }

        setError(cause)
      })
  }, [])

  if (!catalogue || !profileCatalogue || !prefs) {
    return <Busy />
  }

  const setImportance = (field: keyof Importances, value: PreferenceImportance | null) => {
    setPrefs({ ...prefs, importances: { ...prefs.importances, [field]: value } })
    setSaved(false)
  }

  const options = [...catalogue.importanceOptions].sort((a, b) => a.sortOrder - b.sortOrder)

  return (
    <form
      className="page stack"
      onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        setBusy(true)

        api.preferences
          .update({
            version: prefs.version,
            importances: prefs.importances,
            ageMin: prefs.ageMin,
            ageMax: prefs.ageMax,
            heightMin: prefs.heightMin,
            heightMax: prefs.heightMax,
            sets: prefs.sets,
          })
          .then(() => api.preferences.get())
          .then((reloaded) => {
            setPrefs(reloaded)
            setSaved(true)
          })
          .catch(setError)
          .finally(() => setBusy(false))
      }}
    >
      <h1>{t('preferences.title')}</h1>
      <p className="lede">{t('preferences.explain')}</p>

      <ErrorBanner error={error} />
      {saved ? <Notice>{t('common.saved')}</Notice> : null}

      <section className="card">
        <h2>{t('preferences.ageRange')}</h2>
        <div className="range-row">
          <Field label={t('preferences.from')}>
            <NumberInput
              value={prefs.ageMin}
              onChange={(v) => setPrefs({ ...prefs, ageMin: v })}
              min={18}
              max={100}
            />
          </Field>
          <Field label={t('preferences.to')}>
            <NumberInput
              value={prefs.ageMax}
              onChange={(v) => setPrefs({ ...prefs, ageMax: v })}
              min={18}
              max={100}
            />
          </Field>
        </div>

        <h2>{t('preferences.heightRange')}</h2>
        <div className="range-row">
          <Field label={t('preferences.from')}>
            <NumberInput
              value={prefs.heightMin}
              onChange={(v) => setPrefs({ ...prefs, heightMin: v })}
              min={100}
              max={250}
            />
          </Field>
          <Field label={t('preferences.to')}>
            <NumberInput
              value={prefs.heightMax}
              onChange={(v) => setPrefs({ ...prefs, heightMax: v })}
              min={100}
              max={250}
            />
          </Field>
        </div>
      </section>

      <section className="card">
        <h2>{t('preferences.importance')}</h2>

        {IMPORTANCE_FIELDS.map((field) => (
          <Field key={field} label={t(IMPORTANCE_LABEL[field])}>
            <select
              className="control"
              value={prefs.importances[field] ?? ''}
              onChange={(event) =>
                setImportance(
                  field,
                  event.target.value === '' ? null : (event.target.value as PreferenceImportance),
                )
              }
            >
              <option value="">{t('common.choose')}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`importance.${option.value}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
        ))}
      </section>

      <section className="card">
        <h2>{t('profile.nzStatus')}</h2>
        <MultiReferenceSelect
          values={prefs.sets.nzStatusIds}
          onChange={(ids) => setPrefs({ ...prefs, sets: { ...prefs.sets, nzStatusIds: ids } })}
          items={profileCatalogue.nzStatuses}
        />

        <h2>{t('profile.education')}</h2>
        <MultiReferenceSelect
          values={prefs.sets.educationLevelIds}
          onChange={(ids) => setPrefs({ ...prefs, sets: { ...prefs.sets, educationLevelIds: ids } })}
          items={profileCatalogue.educationLevels}
        />

        <h2>{t('profile.maritalStatus')}</h2>
        <MultiReferenceSelect
          values={prefs.sets.maritalStatusIds}
          onChange={(ids) => setPrefs({ ...prefs, sets: { ...prefs.sets, maritalStatusIds: ids } })}
          items={profileCatalogue.maritalStatuses}
        />

        <h2>{t('profile.smoking')}</h2>
        <MultiReferenceSelect
          values={prefs.sets.smokingStatusIds}
          onChange={(ids) => setPrefs({ ...prefs, sets: { ...prefs.sets, smokingStatusIds: ids } })}
          items={profileCatalogue.smokingStatuses}
        />

        <h2>{t('profile.alcohol')}</h2>
        <MultiReferenceSelect
          values={prefs.sets.alcoholStatusIds}
          onChange={(ids) => setPrefs({ ...prefs, sets: { ...prefs.sets, alcoholStatusIds: ids } })}
          items={profileCatalogue.alcoholStatuses}
        />

        <h2>{t('profile.religion')}</h2>
        <MultiReferenceSelect
          values={prefs.sets.religionIds}
          onChange={(ids) => setPrefs({ ...prefs, sets: { ...prefs.sets, religionIds: ids } })}
          items={profileCatalogue.religions}
        />
      </section>

      <button type="submit" className="primary" disabled={busy}>
        {busy ? t('common.saving') : t('common.save')}
      </button>
    </form>
  )
}

function emptyPreferences(): Preferences {
  return {
    version: 0,
    importances: Object.fromEntries(IMPORTANCE_FIELDS.map((f) => [f, null])) as unknown as Importances,
    ageMin: null,
    ageMax: null,
    heightMin: null,
    heightMax: null,
    sets: {
      locationIncludeIds: [],
      locationExcludeIds: [],
      nzStatusIds: [],
      countryGroupIds: [],
      maritalStatusIds: [],
      fertilityOptionIds: [],
      educationLevelIds: [],
      employmentStatusIds: [],
      incomeBandIds: [],
      languageIds: [],
      religionIds: [],
      smokingStatusIds: [],
      alcoholStatusIds: [],
      children: null,
    },
    completedAt: null,
  }
}

function isNotFound(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: number }).status === 404
  )
}
